import { useFlagsStatus } from '@unleash/proxy-client-react'
import { lazy, useMemo } from 'react'
import { Route as LayoutRoute, Navigate, Outlet, Route, Routes, useLocation } from 'react-router'

import { ConditionalComponent } from '@/components/ConditionalComponent/ConditionalComponent'
import { DefaultErrorBoundary } from '@/components/errors/DefaultErrorBoundary/DefaultErrorBoundary'
import { FullPageLoader } from '@/components/layouts/FullPageLoader/FullPageLoader'
import { PageLayout } from '@/components/PageLayout'
import { RetroButtons } from '@/components/RetroButtons'
import { withSuspense } from '@/components/WithSuspense/WithSuspense'
import { PermissionsWrapper } from '@/contexts/AppWrapper'
import { useCognitoAuth } from '@/contexts/cognitoAuthContext'
import { useMarketIntelligencePermissions } from '@/hooks/permissions/marketIntelligence/useMarketIntelligencePermissions'
import { useTemplateManagementPermissions } from '@/hooks/permissions/templateManagement/useTemplateManagementPermissions'
import { useSelfServeAndPermissions } from '@/hooks/permissions/useSelfServeAndPermissions'
import { useToastIntegrationPermissions } from '@/hooks/permissions/useToastIntegrationPermissions'
import { useFlag } from '@/hooks/useFlag'
import { usePaymentStatusWarnings } from '@/hooks/usePaymentStatusWarnings'
import { AuthLayout } from '@/pages/Auth'
import { MarketIntelligencePages } from '@/routes/MarketIntelligencePages'
import { CoreLevelFeatureNames } from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import { PremiumFeatures } from '@/services/types/premiumFeatures/premiumFeatures'
import { useCorePermissions, usePermissionsStore } from '@/stores/permissions/permissions'
import {
  usePremiumFeaturesPermissions,
  usePremiumFeaturesStatus,
  usePremiumFeaturesStore,
} from '@/stores/permissions/premiumFeatures'
import { AvailableProviders } from '@/utils/constants/providers/availableProviders'
import { DashboardExperiments } from '../experiments'
import { MenusPageTabs } from './MenusPageTabs'
import { PATHS } from './PATHS'

// route-based code splitting
// https://reactjs.org/docs/code-splitting.html#route-based-code-splitting
const AccountSuspendedPage = withSuspense(lazy(() => import('@/pages/AccountSuspended')))
const AccountConnectionsPage = withSuspense(lazy(() => import('@/pages/AccountConnections')))
const ToastConnectionSetupPage = withSuspense(
  lazy(() => import('@/pages/IntegrationPOS/Toast/SetupPage')),
)
const ToastConnectionResultPage = withSuspense(
  lazy(() => import('@/pages/IntegrationPOS/Toast/ResultPage')),
)
const ToastConnectionDetailsPage = withSuspense(
  lazy(() => import('@/pages/IntegrationPOS/Toast/DetailsPage')),
)
const UserProfilePage = withSuspense(lazy(() => import('@/pages/UserProfile')))
const BillingAndPaymentsPage = withSuspense(
  lazy(() => import('@/pages/BillingAndPaymentsPage/BillingAndPaymentsPage')),
)
const AnalyticsPage = withSuspense(lazy(() => import('@/pages/Analytics')))
const LoginPage = withSuspense(lazy(() => import('@/pages/Auth/Login')))
const ForgotPasswordPage = withSuspense(lazy(() => import('@/pages/Auth/ForgotPassword')))
const ResetPasswordPage = withSuspense(lazy(() => import('@/pages/Auth/ResetPassword')))
const NewPasswordPage = withSuspense(lazy(() => import('@/pages/Auth/NewPassword')))
const SignUpPage = withSuspense(lazy(() => import('@/pages/Auth/Signup')))
const ChoiceDetailsPage = withSuspense(lazy(() => import('@/pages/ChoiceDetails')))
const ForbiddenPage = withSuspense(lazy(() => import('@/pages/Forbidden')))
const ListingsPage = withSuspense(lazy(() => import('@/pages/Listings/ListingsPage')))
const OmniLocationDetailsPage = withSuspense(lazy(() => import('@/pages/OmniLocationDetails')))
const LocationsPage = withSuspense(lazy(() => import('@/pages/Locations')))
const MenuDetailsPage = withSuspense(lazy(() => import('@/pages/MenuDetails/MenuDetailsPage')))
const HomePageV1 = withSuspense(lazy(() => import('@/pages/Home/v1')))
const HomePageV2 = withSuspense(lazy(() => import('@/pages/Home/v2/HomePage')))
const MenusPage = withSuspense(lazy(() => import('@/pages/Menus')))
const ModifierDetailsPage = withSuspense(lazy(() => import('@/pages/ModifierDetails')))
const NotFoundPage = withSuspense(lazy(() => import('@/pages/NotFound')))
const ReviewsPage = withSuspense(lazy(() => import('@/pages/Reviews')))
const ReviewLabelsPage = withSuspense(lazy(() => import('@/pages/ReviewLabels')))
const ReviewTemplatesPage = withSuspense(lazy(() => import('@/pages/ReviewTemplates')))
const ReviewTemplatePage = withSuspense(lazy(() => import('@/pages/ReviewTemplate')))
const ReviewNotificationsPage = withSuspense(lazy(() => import('@/pages/ReviewNotifications')))
const ReviewNotificationPage = withSuspense(lazy(() => import('@/pages/ReviewNotification')))
const ReviewsInsightsPage = withSuspense(
  lazy(() => import('@/pages/ReviewsInsights/ReviewInsightsPage')),
)
const ReviewAnalysisContent = withSuspense(
  lazy(() => import('@/pages/ReviewsInsights/ReviewAnalysis')),
)
const SentimentAnalysisContent = withSuspense(
  lazy(() => import('@/pages/ReviewsInsights/SentimentAnalysis')),
)
const KeywordAnalysisContent = withSuspense(
  lazy(() => import('@/pages/ReviewsInsights/KeywordAnalysis')),
)
const SectionDetailsPage = withSuspense(lazy(() => import('@/pages/SectionDetails')))
const EmailReportsPage = withSuspense(lazy(() => import('@/pages/EmailReports')))
const EmailReportPage = withSuspense(lazy(() => import('@/pages/EmailReport')))
const ReviewResponsePromptsPage = withSuspense(lazy(() => import('@/pages/ReviewResponsePrompts')))
const ReviewsResponseSettings = withSuspense(lazy(() => import('@/pages/ReviewsResponseSettings')))
const SearchAppearancesPage = withSuspense(
  lazy(() => import('@/pages/SearchAppearances/SearchAppearancesPage')),
)
const SocialManagementPage = withSuspense(
  lazy(() => import('@/pages/socialManagement/SocialManagementPage')),
)
const PublishedTab = withSuspense(
  lazy(
    () =>
      import(
        '@/pages/socialManagement/SocialManagementPage/tabs/PublishedTabContent/PublishedTabContent'
      ),
  ),
)
const ScheduledTab = withSuspense(
  lazy(
    () =>
      import(
        '@/pages/socialManagement/SocialManagementPage/tabs/ScheduledTabContent/ScheduledTabContent'
      ),
  ),
)
const DraftsTab = withSuspense(
  lazy(
    () =>
      import(
        '@/pages/socialManagement/SocialManagementPage/tabs/DraftsTabContent/DraftsTabContent'
      ),
  ),
)
const ChoosePublisherPage = withSuspense(
  lazy(() => import('@/pages/socialManagement/ChoosePublisherPage')),
)
const CreatePostPage = withSuspense(
  lazy(() => import('@/pages/socialManagement/postFormPages/CreatePostPage')),
)
const EditPostPage = withSuspense(
  lazy(() => import('@/pages/socialManagement/postFormPages/EditPostPage')),
)
const UserManagementPage = withSuspense(lazy(() => import('@/pages/UserManagement')))

const HourWidgetPage = withSuspense(lazy(() => import('@/pages/Widget/Hour')))
const MenuWidgetPage = withSuspense(lazy(() => import('@/pages/Widget/Menu')))
const WidgetsFTEPage = withSuspense(lazy(() => import('@/pages/WidgetsFte')))
const WidgetsListPage = withSuspense(lazy(() => import('@/pages/WidgetsList')))

const FirstPartyReviewsFTEPage = withSuspense(lazy(() => import('@/pages/FirstPartyReviews/FTE')))
const FirstPartyReviewsListPage = withSuspense(lazy(() => import('@/pages/FirstPartyReviews/List')))
const FirstPartyReviewsFormPage = withSuspense(lazy(() => import('@/pages/FirstPartyReviews/Form')))
const FirstPartyReviewsUpsellPage = withSuspense(
  lazy(() => import('@/pages/FirstPartyReviews/Upsell')),
)
const ReviewGenerationPage = withSuspense(lazy(() => import('@/pages/ReviewGeneration')))

const NoStoreLocationsPage = withSuspense(lazy(() => import('@/pages/NoStoreLocations')))

const MarketIntelligencePage = withSuspense(
  lazy(() => import('@/pages/MarketIntelligence/MarketIntelligencePage/MarketIntelligencePage')),
)
const MonitoringCompetitorsPage = withSuspense(
  lazy(
    () => import('@/pages/MarketIntelligence/MonitoringCompetitorsPage/MonitoringCompetitorsPage'),
  ),
)
const UpgradePackagePage = withSuspense(
  lazy(() => import('@/pages/UpgradePackage/UpgradePackagePage')),
)
const OnboardingPage = withSuspense(lazy(() => import('@/pages/Onboarding/OnboardingPage')))

const RootRoute = () => <Navigate to={PATHS.homePage} replace />

const ProtectedRoute = () => {
  const { isAuthenticated, isAuthenticatedPreviousValue } = useCognitoAuth()
  const { pathname, search } = useLocation()

  const {
    canDoUpgrade,
    shouldDoUpgrade,
    shouldDoOnboarding,
    isAccountInfoLoading,
    isSelfServiceInfoLoading,
  } = useSelfServeAndPermissions()

  const shouldEnforceBillingFoundations = useFlag(
    DashboardExperiments.shouldEnforceBillingFoundations,
  )
  const premiumFeaturesStatus = usePremiumFeaturesStatus()
  const showOnboardingScreenFlag = useFlag(DashboardExperiments.onboarding)
  const showNoLocationsScreenFlag = useFlag(DashboardExperiments.showNoLocationsScreen)
  const showUpgradePackageScreenFlag = useFlag(DashboardExperiments.upgradePackagePage)

  // TODO: Remove this once the flag is removed
  const onboardPath = showOnboardingScreenFlag
    ? PATHS.onboardingPage // self-serve onboarding
    : showNoLocationsScreenFlag
      ? PATHS.noLocations // scheduled onboarding
      : null // no onboarding

  const canDoOnboarding = !!onboardPath

  const encodedRedirectUri = encodeURIComponent(pathname + search)
  const redirectUriSearchParam = isAuthenticatedPreviousValue
    ? ''
    : `?redirect_uri=${encodedRedirectUri}`

  const shouldBlockAccount =
    premiumFeaturesStatus &&
    premiumFeaturesStatus === 'SUSPENDED' &&
    shouldEnforceBillingFoundations

  const endpointsToWaitFor = [isSelfServiceInfoLoading, isAccountInfoLoading]
  const selfServiceFlags = [
    showOnboardingScreenFlag,
    showUpgradePackageScreenFlag,
    showNoLocationsScreenFlag,
  ]
  const isAnyEndpointLoading = endpointsToWaitFor.some(Boolean)
  const isAnyFlagEnabled = selfServiceFlags.some(Boolean)

  const shouldWaitDataBeforeRender = isAnyFlagEnabled && isAnyEndpointLoading

  if (shouldWaitDataBeforeRender) {
    return <FullPageLoader />
  }

  // Here we can also have routes access control for specific roles in the future
  if (!isAuthenticated) {
    return <Navigate to={PATHS.signInPage + redirectUriSearchParam} replace />
  }

  if (shouldBlockAccount && PATHS.accountSuspended !== pathname) {
    return <Navigate to={PATHS.accountSuspended} replace />
  }

  if (!shouldBlockAccount && PATHS.accountSuspended === pathname) {
    return <Navigate to={PATHS.homePage} replace />
  }

  if (canDoUpgrade && shouldDoUpgrade && pathname !== PATHS.upgradePackagePage) {
    return <Navigate to={PATHS.upgradePackagePage} replace />
  }

  if (canDoOnboarding && (!canDoUpgrade || !shouldDoUpgrade)) {
    if (pathname !== onboardPath && shouldDoOnboarding) {
      return <Navigate to={onboardPath} replace />
    }

    if (pathname === onboardPath && !shouldDoOnboarding) {
      return <Navigate to={PATHS.homePage} replace />
    }
  }

  return <Outlet />
}

export function AppRoutes() {
  const { isMarqiiUser } = useCognitoAuth()
  const { flagsReady, flagsError } = useFlagsStatus()
  const { isLoading: isPremiumFeaturesPermissionsLoading } = usePremiumFeaturesStore()
  const { isLoading: isRolesAndPermissionsLoading } = usePermissionsStore()

  const { shouldShowBillingAndPayments, canDoUpgrade, isAccountInfoLoading } =
    useSelfServeAndPermissions()

  const { shouldShowTemplatesManagement } = useTemplateManagementPermissions()
  const { shouldShowMarketIntelligencePages } = useMarketIntelligencePermissions()

  const { canManageToastIntegration, isLoading: isToastIntegrationPermissionsLoading } =
    useToastIntegrationPermissions()

  const onboardingFlag = useFlag(DashboardExperiments.onboarding)
  const selfServeHomePageFlag = useFlag(DashboardExperiments.selfServeHomePage)
  const hideMenusForLocationManagersFlag = useFlag(
    DashboardExperiments.hideMenusForLocationManagers,
  )
  const showSelfServiceSignUp = useFlag(DashboardExperiments.showSelfServiceSignUp)
  const reviewGenerationFlag = useFlag(DashboardExperiments.reviewGeneration)
  const shouldEnforceBillingFoundations = useFlag(
    DashboardExperiments.shouldEnforceBillingFoundations,
  )
  const showNoLocationsScreen = useFlag(DashboardExperiments.showNoLocationsScreen)

  const responsePromptsPermission = useCorePermissions(CoreLevelFeatureNames.promptManagement)
  const userManagementPermission = useCorePermissions(CoreLevelFeatureNames.userManagement)
  const reportsPermission = useCorePermissions(CoreLevelFeatureNames.emailedReports)
  const menuManagementPermission = useCorePermissions(CoreLevelFeatureNames.menuManagement)
  const labelManagementPermission = useCorePermissions(CoreLevelFeatureNames.labelManagement)
  const socialManagementPermission = useCorePermissions(CoreLevelFeatureNames.socialManagementPosts)
  const listingsManagementPermission = useCorePermissions(CoreLevelFeatureNames.listingsManagement)

  const premiumFeaturesPermissions = usePremiumFeaturesPermissions()
  const premiumFeaturesStatus = usePremiumFeaturesStatus()

  usePaymentStatusWarnings()

  const hasReviewMonitoringFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewMonitoring,
  )
  const hasManagedReviewsInternalFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ManagedReviewsInternal,
  )
  const hasManagedReviewsExternalFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ManagedReviewsExternal,
  )

  const hasMenusFeature = premiumFeaturesPermissions.includes(PremiumFeatures.Menus)
  const hasListingsFeature = premiumFeaturesPermissions.includes(PremiumFeatures.Listings)
  const hasSocialManagementFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.SocialManagement,
  )
  const hasWidgetMenusFeature =
    hasMenusFeature && premiumFeaturesPermissions.includes(PremiumFeatures.WidgetMenus)
  const hasWidgetHoursFeature = premiumFeaturesPermissions.includes(PremiumFeatures.WidgetHours)
  const hasWidgetReviewFormsFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.WidgetReviewForms,
  )
  const hasReviewGenerationFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewGeneration,
  )
  const hasReportsFeature = hasListingsFeature || hasReviewMonitoringFeature

  const hasReportsPermission = useMemo(() => {
    return !!reportsPermission?.edit
  }, [reportsPermission])

  const hasUserManagementPermission = useMemo(() => {
    return !!userManagementPermission?.edit
  }, [userManagementPermission])

  const hasMenuManagementPermission = useMemo(() => {
    return !!menuManagementPermission?.edit
  }, [menuManagementPermission])

  const hasLabelManagementPermission = useMemo(() => {
    return !!labelManagementPermission?.edit
  }, [labelManagementPermission])

  const hasSocialManagementPermission = useMemo(() => {
    return !!socialManagementPermission?.view
  }, [socialManagementPermission])

  const hasHoursWidgetsManagementPermission = useMemo(() => {
    return !listingsManagementPermission?.hide
  }, [listingsManagementPermission])

  const hasMenusWidgetsManagementPermission = useMemo(() => {
    return !menuManagementPermission?.hide
  }, [menuManagementPermission])

  const hasFirstPartyReviewsPermission = useMemo(() => {
    return !listingsManagementPermission?.hide
  }, [listingsManagementPermission])

  const isLocationManager = hasMenuManagementPermission && !hasUserManagementPermission

  const shouldHideMenusForLocationManagers = hideMenusForLocationManagersFlag && isLocationManager

  const hasManagedReviewsInternalFeatureAndIsMarqiiUser =
    isMarqiiUser && hasManagedReviewsInternalFeature

  const hasEditPromptPermission = !!responsePromptsPermission?.edit
  const hasManagedReviewsExternalPermissions =
    hasManagedReviewsExternalFeature && hasEditPromptPermission

  const shouldShowResponsePrompts =
    hasManagedReviewsInternalFeatureAndIsMarqiiUser || hasManagedReviewsExternalPermissions

  const shouldShowLabelManagementPage = hasReviewMonitoringFeature && hasLabelManagementPermission

  const shouldShowSocialManagementPage = hasSocialManagementFeature && hasSocialManagementPermission

  const shouldShowWidgets =
    (hasWidgetMenusFeature && hasMenusWidgetsManagementPermission) ||
    (hasWidgetHoursFeature && hasHoursWidgetsManagementPermission)

  const shouldShowFirstPartyReviewsPage =
    hasWidgetReviewFormsFeature && hasFirstPartyReviewsPermission

  const shouldShowUpsellPage = hasReviewMonitoringFeature && !hasWidgetReviewFormsFeature

  const isPermissionsLoading = isPremiumFeaturesPermissionsLoading || isRolesAndPermissionsLoading

  const shouldShowReviewGenerationPage = hasReviewGenerationFeature && reviewGenerationFlag

  const shouldBlockAccount =
    premiumFeaturesStatus &&
    premiumFeaturesStatus === 'SUSPENDED' &&
    shouldEnforceBillingFoundations

  const appInitAndReady = flagsReady || flagsError

  if (flagsError) {
    // track event in NR
    console.error('Unleash flags in error state', { flagsError })
  }

  return (
    <ConditionalComponent condition={appInitAndReady}>
      <RetroButtons />

      <PermissionsWrapper>
        <Routes>
          <LayoutRoute element={<AuthLayout />}>
            <Route path={PATHS.signInPage} element={<LoginPage />} />
            <Route path={PATHS.forgotPasswordPage} element={<ForgotPasswordPage />} />
            <Route path={PATHS.resetPasswordPage} element={<ResetPasswordPage />} />
            <Route path={PATHS.newPasswordPage} element={<NewPasswordPage />} />
            {showSelfServiceSignUp && <Route path={PATHS.signUpPage} element={<SignUpPage />} />}
          </LayoutRoute>

          <LayoutRoute element={<ProtectedRoute />}>
            {shouldBlockAccount && (
              <Route
                path={PATHS.accountSuspended}
                element={
                  <DefaultErrorBoundary showLink={false} context="ACCOUNT-SUSPENDED-PAGE">
                    <AccountSuspendedPage />
                  </DefaultErrorBoundary>
                }
              />
            )}

            {onboardingFlag && <Route path={PATHS.onboardingPage} element={<OnboardingPage />} />}

            {/* TODO: Remove this once the flag is removed */}
            {showNoLocationsScreen && (
              <Route path={PATHS.noLocations} element={<NoStoreLocationsPage />} />
            )}

            <LayoutRoute element={<PageLayout />}>
              <Route path={PATHS.root} element={<RootRoute />} />

              <Route
                path={PATHS.homePage}
                element={selfServeHomePageFlag ? <HomePageV2 /> : <HomePageV1 />}
              />

              <Route path={PATHS.accountConnectionsPage} element={<AccountConnectionsPage />} />

              {(isPermissionsLoading ||
                canManageToastIntegration ||
                isToastIntegrationPermissionsLoading) && (
                <>
                  <Route path={PATHS.toastConnectionSetup} element={<ToastConnectionSetupPage />} />
                  <Route
                    path={PATHS.toastConnectionResult}
                    element={<ToastConnectionResultPage />}
                  />
                  <Route
                    path={PATHS.toastConnectionCreate}
                    element={<ToastConnectionDetailsPage create />}
                  />
                  <Route
                    path={PATHS.toastConnectionDetails}
                    element={<ToastConnectionDetailsPage />}
                  />
                </>
              )}

              {(isPermissionsLoading || hasUserManagementPermission) && (
                <Route path={PATHS.userManagementPage} element={<UserManagementPage />} />
              )}

              {(isPermissionsLoading || shouldShowSocialManagementPage) && (
                <>
                  <LayoutRoute element={<SocialManagementPage />}>
                    <Route path={PATHS.publishedTab} element={<PublishedTab />} />
                    <Route path={PATHS.scheduledTab} element={<ScheduledTab />} />
                    <Route path={PATHS.draftsTab} element={<DraftsTab />} />
                  </LayoutRoute>
                  <Route path={PATHS.choosePublisher} element={<ChoosePublisherPage />} />
                  <Route
                    path={PATHS.createFacebookPost}
                    element={<CreatePostPage publisher={AvailableProviders.Facebook} />}
                  />
                  <Route
                    path={PATHS.createGooglePost}
                    element={<CreatePostPage publisher={AvailableProviders.Google} />}
                  />
                  <Route
                    path={PATHS.createApplePost}
                    element={<CreatePostPage publisher={AvailableProviders.Apple} />}
                  />
                  <Route path={PATHS.editPost} element={<EditPostPage />} />
                </>
              )}

              {(isPermissionsLoading || canDoUpgrade) && (
                <Route path={PATHS.upgradePackagePage} element={<UpgradePackagePage />} />
              )}

              <Route path={PATHS.userProfilePage} element={<UserProfilePage />} />

              {(isPermissionsLoading || isAccountInfoLoading || shouldShowBillingAndPayments) && (
                <Route path={PATHS.billingAndPaymentsPage} element={<BillingAndPaymentsPage />} />
              )}

              {(isPermissionsLoading || hasListingsFeature) && (
                <>
                  <Route path={PATHS.listingsPage} element={<ListingsPage />} />
                  <Route path={PATHS.analyticsPage} element={<AnalyticsPage />} />
                  <Route path={PATHS.searchAppearancesPage} element={<SearchAppearancesPage />} />
                </>
              )}

              <Route path={PATHS.locationsPage} element={<LocationsPage />} />

              <Route path={PATHS.locationDetailsPage} element={<OmniLocationDetailsPage />} />

              {(isPermissionsLoading || hasMenusFeature) && (
                <>
                  {!shouldHideMenusForLocationManagers && (
                    <>
                      <Route
                        path={PATHS.menusPage}
                        element={<MenusPage selectedTab={MenusPageTabs.menusPage} />}
                      />
                      <Route
                        path={PATHS.sectionsPage}
                        element={<MenusPage selectedTab={MenusPageTabs.sectionsPage} />}
                      />
                      <Route
                        path={PATHS.itemsPage}
                        element={<MenusPage selectedTab={MenusPageTabs.itemsPage} />}
                      />
                      <Route
                        path={PATHS.choicesPage}
                        element={<MenusPage selectedTab={MenusPageTabs.choicesPage} />}
                      />
                      <Route
                        path={PATHS.modifiersPage}
                        element={<MenusPage selectedTab={MenusPageTabs.modifiersPage} />}
                      />
                    </>
                  )}

                  <Route path={PATHS.menuDetailsPage} element={<MenuDetailsPage />} />
                  <Route path={PATHS.sectionDetailsPage} element={<SectionDetailsPage />} />
                  <Route path={PATHS.modifierDetailsPage} element={<ModifierDetailsPage />} />
                  <Route path={PATHS.choiceDetailsPage} element={<ChoiceDetailsPage />} />
                </>
              )}

              {(isPermissionsLoading || shouldShowMarketIntelligencePages) && (
                <>
                  <Route
                    path={PATHS[MarketIntelligencePages.locationsTable]}
                    element={<MarketIntelligencePage />}
                  />
                  <Route
                    path={PATHS[MarketIntelligencePages.competitorsByLocations]}
                    element={<MonitoringCompetitorsPage />}
                  />
                </>
              )}

              {(isPermissionsLoading || hasReviewMonitoringFeature) && (
                <Route path={PATHS.reviewsPage} element={<ReviewsPage />} />
              )}

              {(isPermissionsLoading || shouldShowReviewGenerationPage) && (
                <Route path={PATHS.reviewGenerationPage} element={<ReviewGenerationPage />} />
              )}

              {(isPermissionsLoading || shouldShowLabelManagementPage) && (
                <Route path={PATHS.reviewLabelsPage} element={<ReviewLabelsPage />} />
              )}

              {(isPermissionsLoading || shouldShowTemplatesManagement) && (
                <>
                  <Route path={PATHS.reviewTemplatesPage} element={<ReviewTemplatesPage />} />
                  <Route path={PATHS.reviewTemplatePage} element={<ReviewTemplatePage />} />
                </>
              )}

              {(isPermissionsLoading || shouldShowResponsePrompts) && (
                <>
                  <Route
                    path={PATHS.reviewResponsePromptsPage}
                    element={<ReviewResponsePromptsPage />}
                  />

                  <Route path={PATHS.reviewResponsePrompt} element={<ReviewsResponseSettings />} />
                </>
              )}

              {(isPermissionsLoading || hasReviewMonitoringFeature) && (
                <>
                  <Route
                    path={PATHS.reviewNotificationsPage}
                    element={<ReviewNotificationsPage />}
                  />
                  <Route path={PATHS.reviewNotification} element={<ReviewNotificationPage />} />
                </>
              )}

              {(isPermissionsLoading || (hasReportsPermission && hasReportsFeature)) && (
                <>
                  <Route path={PATHS.emailReportsPage} element={<EmailReportsPage />} />
                  <Route path={PATHS.emailReport} element={<EmailReportPage />} />
                </>
              )}

              {(isPermissionsLoading || shouldShowWidgets) && (
                <>
                  <Route path={PATHS.widgetFte} element={<WidgetsFTEPage />} />

                  {hasWidgetHoursFeature && hasHoursWidgetsManagementPermission && (
                    <Route path={PATHS.hourWidget} element={<HourWidgetPage />} />
                  )}

                  {hasWidgetMenusFeature && hasMenusWidgetsManagementPermission && (
                    <Route path={PATHS.menuWidget} element={<MenuWidgetPage />} />
                  )}

                  <Route path={PATHS.widgetsList} element={<WidgetsListPage />} />
                </>
              )}

              {(isPermissionsLoading || shouldShowFirstPartyReviewsPage) && (
                <>
                  <Route path={PATHS.firstPartyReviewsFte} element={<FirstPartyReviewsFTEPage />} />

                  <Route path={PATHS.firstPartyReviews} element={<FirstPartyReviewsListPage />} />

                  <Route
                    path={PATHS.firstPartyReviewsForm}
                    element={<FirstPartyReviewsFormPage />}
                  />
                </>
              )}

              {(isPermissionsLoading || shouldShowUpsellPage) && (
                <Route
                  path={PATHS.firstPartyReviewsUpsell}
                  element={<FirstPartyReviewsUpsellPage />}
                />
              )}

              <LayoutRoute element={<ReviewsInsightsPage />}>
                <Route path={PATHS.reviewAnalysis} element={<ReviewAnalysisContent />} />
                <Route path={PATHS.sentimentAnalysis} element={<SentimentAnalysisContent />} />

                <Route path={PATHS.keywordAnalysis} element={<KeywordAnalysisContent />} />
              </LayoutRoute>

              <Route path={PATHS.forbiddenPage} element={<ForbiddenPage />} />
              <Route path={PATHS.notFoundPage} element={<NotFoundPage />} />
              <Route path={PATHS.anyOtherPath} element={<NotFoundPage />} />
            </LayoutRoute>
          </LayoutRoute>
        </Routes>
      </PermissionsWrapper>
    </ConditionalComponent>
  )
}
